import { ref, computed, watch, onMounted, nextTick } from 'vue';
import type { RequestContent } from '../types';
import { useRequestContents } from './useRequestContents';
import { useDebug } from '@/composables/useDebug';

const { debug } = useDebug('requestContent:editUrl');

interface UseRequestContentEditUrlOptions {
  item: RequestContent;
  domain: string;
}

export const useRequestContentEditUrl = (options: UseRequestContentEditUrlOptions) => {
  const { item, domain } = options;
  const { updateRequestContent } = useRequestContents();

  // Form state
  const customUrl = ref('');
  const urlInput = ref();

  // Original URL from the item
  const originalUrl = computed(() => {
    return item.customUrl || item.slug;
  });

  // URL preview
  const urlPreview = computed(() => {
    const url = customUrl.value.trim() || originalUrl.value;
    return `${domain}/re/${url}`;
  });

  // Clean up URL input (lowercase, replace spaces with hyphens, remove special chars)
  const cleanupUrl = (value: string): string => {
    return value
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9_-]/gi, '');
  };

  // Watch for URL changes to clean up the input
  watch(customUrl, newValue => {
    // Clean up the URL
    const cleaned = cleanupUrl(newValue);
    if (cleaned !== newValue) {
      customUrl.value = cleaned;
    }
  });

  // Initialize form
  const initializeForm = () => {
    customUrl.value = originalUrl.value;
    nextTick(() => {
      // FormKit uses different structure for accessing the input element
      if (urlInput.value) {
        const input = urlInput.value?.node?.context?.node?.input?.input;
        if (input && typeof input.focus === 'function') {
          input.focus();
        }
      }
    });
  };

  // Submit handler with validation guard
  const handleSubmit = async (formState: any): Promise<RequestContent | null> => {
    // Guard clause to prevent execution when button is disabled
    if (!formState.valid || !formState.dirty) {
      return null;
    }

    try {
      // Only send the changed customUrl field (camelCase - will be converted to snake_case by API)
      const changedFields = {
        customUrl: customUrl.value.trim() || null,
      };

      const updatedItem = await updateRequestContent(item.id, changedFields);

      return updatedItem;
    } catch (error: any) {
      debug('Failed to update URL:', error);
      throw error;
    }
  };

  // Initialize on mount
  onMounted(() => {
    initializeForm();
  });

  return {
    // State
    customUrl,
    urlInput,

    // Computed
    originalUrl,
    urlPreview,

    // Methods
    handleSubmit,
    initializeForm,
  };
};
